import type { CalendarAccommodation } from './types';
import { toYmd, addDays, fmtPretty, daysBetween } from './pricing';

export interface FilterState {
    cityId: string;
    offerIdFilter: number | null;
    from: string;
    to: string;
    search: string;
    types: Set<string>;
    services: Set<string>;
    classifs: Set<string>;
    states: Set<string>;
    activeOnly: boolean;
    hasPriceOnly: boolean;
    showPartners: boolean;
    partnerAgencyFilter: number | null;
}

export type FilterChangeCallback = (state: FilterState) => void;

const UNIT_TYPES = ['A1', 'A2', 'A2+1', 'A3', 'A4', 'A4+1', 'A6', 'Studio'];
const SERVICES   = ['SN', 'BB', 'HB', 'FB', 'AI'];
const CLASSIFS   = ['Classic', 'Standard', 'Business', 'Premium', 'Luxury'];

let _state: FilterState;
let _onChange: FilterChangeCallback;
let _accoms: CalendarAccommodation[] = [];
let _allCities: string[] = [];
let _i18n: Record<string, string> = {};
let _monthNames: string[] = [];
let _container: HTMLElement;

let _pickerOpen = false;
let _pickerPending: { from: string | null; to: string | null } = { from: null, to: null };

export function initFilters(
    container: HTMLElement,
    cities: string[],
    defaultFrom: string,
    defaultTo: string,
    i18n: Record<string, string>,
    monthNames: string[],
    onChange: FilterChangeCallback
): FilterState {
    _container = container;
    _allCities = cities;
    _i18n = i18n;
    _monthNames = monthNames;
    _onChange = onChange;
    _state = {
        cityId: cities[0] ?? '',
        offerIdFilter: null,
        from: defaultFrom,
        to: defaultTo,
        search: '',
        types: new Set(),
        services: new Set(),
        classifs: new Set(),
        states: new Set(['free', 'booked', 'option', 'blocked', 'no-price']),
        activeOnly: true,
        hasPriceOnly: false,
        showPartners: true,
        partnerAgencyFilter: null,
    };
    return _state;
}

export function updateAccommodations(accoms: CalendarAccommodation[]): void {
    _accoms = accoms;
    rebuildAccomSelect();
    rebuildPartnerSelect();
}

export function renderFilterBar(container: HTMLElement): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'filter-bar';
    bar.innerHTML = buildFilterBarHtml(_state);
    container.prepend(bar);

    wireFilterBar(bar);
    return bar;
}

export function updateFilterBar(bar: HTMLElement): void {
    // Update period display and nav buttons only (rest is user-driven)
    const periodBtn = bar.querySelector<HTMLButtonElement>('.js-period-trigger');
    if (periodBtn) periodBtn.textContent = periodLabel(_state.from, _state.to);
}

export function getState(): FilterState { return _state; }

function buildFilterBarHtml(state: FilterState): string {
    const stateKeys = [
        { key: 'free',     label: _i18n['free']     || 'Free',     swatch: '#ffffff', border: '#d1d7e0' },
        { key: 'booked',   label: _i18n['booked']   || 'Booked',   swatch: '#1e2a45' },
        { key: 'option',   label: _i18n['optioned'] || 'Optioned', swatch: '#e0a82e' },
        { key: 'blocked',  label: _i18n['blocked']  || 'Blocked',  swatch: 'repeating-linear-gradient(45deg,#adb5c8 0 4px,#c5cad8 4px 8px)' },
        { key: 'no-price', label: _i18n['noPrice']  || 'No price', swatch: '#f5f8fb', border: '#d1d7e0' },
    ];

    return `
      <div class="filter-field">
        <label class="filter-label">${_i18n['city'] || 'City'}</label>
        <select class="filter-select js-city">
          ${_allCities.map(c => `<option value="${esc(c)}"${c === state.cityId ? ' selected' : ''}>${esc(c)}</option>`).join('')}
        </select>
      </div>

      <div class="filter-field js-accom-field">
        <label class="filter-label">${_i18n['accommodation'] || 'Accommodation'}</label>
        <select class="filter-select js-accom">
          <option value="">${esc(allInCityLabel())}</option>
        </select>
      </div>

      <div class="filter-field js-partner-field">
        <label class="filter-label">${_i18n['partners'] || 'Partners'}</label>
        <select class="filter-select js-partner-agency"${!state.showPartners ? ' disabled' : ''}>
          <option value="">${esc(_i18n['allPartners'] || 'All partners')}</option>
        </select>
      </div>

      <div class="filter-field" style="position:relative">
        <label class="filter-label">${_i18n['period'] || 'Period'}</label>
        <div style="position:relative">
          <button type="button" class="drp-trigger js-period-trigger${state.from && state.to ? ' has-value' : ''}">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="3" width="12" height="11" rx="1.5"/><path d="M5 1v4M11 1v4M2 7h12" stroke-linecap="round"/></svg>
            ${esc(periodLabel(state.from, state.to))}
          </button>
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-label">${_i18n['searchUnit'] || 'Search unit'}</label>
        <input class="filter-input filter-input-search js-search" placeholder="Studio, Apt…" value="${esc(state.search)}">
      </div>

      <div class="filter-field">
        <label class="filter-label">${_i18n['unitType'] || 'Unit type'}</label>
        <div class="chip-row js-chips-type">
          ${UNIT_TYPES.map(t => chip(t, state.types.has(t))).join('')}
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-label">${_i18n['service'] || 'Service'}</label>
        <div class="chip-row js-chips-service">
          ${SERVICES.map(s => chip(s, state.services.has(s))).join('')}
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-label">${_i18n['classification'] || 'Classification'}</label>
        <div class="chip-row js-chips-classif">
          ${CLASSIFS.map(c => chip(c, state.classifs.has(c))).join('')}
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-label">${_i18n['state'] || 'State'}</label>
        <div class="chip-row js-chips-state">
          ${stateKeys.map(s =>
              `<span class="chip js-chip-state${state.states.has(s.key) ? ' on' : ''}" data-key="${s.key}">
                 <span class="swatch" style="background:${s.swatch};border-color:${s.border ?? 'transparent'}"></span>
                 ${esc(s.label)}
               </span>`
          ).join('')}
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-label">More</label>
        <div style="display:flex;gap:14px;flex-wrap:wrap">
          <label class="toggle-mini"><input type="checkbox" class="js-active-only"${state.activeOnly ? ' checked' : ''}> ${esc(_i18n['activeOnly'] || 'Active only')}</label>
          <label class="toggle-mini"><input type="checkbox" class="js-has-price"${state.hasPriceOnly ? ' checked' : ''}> ${esc(_i18n['hasPrice'] || 'Has price')}</label>
          <label class="toggle-mini"><input type="checkbox" class="js-partners-on"${state.showPartners ? ' checked' : ''}> ${esc(_i18n['showPartnerOffers'] || 'Show partner offers')}</label>
        </div>
      </div>`;
}

function wireFilterBar(bar: HTMLElement): void {
    // City
    bar.querySelector<HTMLSelectElement>('.js-city')?.addEventListener('change', e => {
        _state = { ..._state, cityId: (e.target as HTMLSelectElement).value, offerIdFilter: null, partnerAgencyFilter: null };
        rebuildAccomSelect();
        rebuildPartnerSelect();
        emit();
    });

    // Accom
    bar.querySelector<HTMLSelectElement>('.js-accom')?.addEventListener('change', e => {
        const val = (e.target as HTMLSelectElement).value;
        _state = { ..._state, offerIdFilter: val ? parseInt(val) : null };
        emit();
    });

    // Period trigger — opens inline popover
    bar.querySelector<HTMLButtonElement>('.js-period-trigger')?.addEventListener('click', e => {
        e.stopPropagation();
        togglePicker(bar);
    });

    // Search
    let searchTimer: ReturnType<typeof setTimeout>;
    bar.querySelector<HTMLInputElement>('.js-search')?.addEventListener('input', e => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => {
            _state = { ..._state, search: (e.target as HTMLInputElement).value };
            emit();
        }, 250);
    });

    // Chip toggles
    wireChips(bar, '.js-chips-type .chip', 'types');
    wireChips(bar, '.js-chips-service .chip', 'services');
    wireChips(bar, '.js-chips-classif .chip', 'classifs');

    // State chips
    bar.querySelectorAll<HTMLElement>('.js-chip-state').forEach(el => {
        el.addEventListener('click', () => {
            const key = el.dataset.key!;
            const next = new Set(_state.states);
            next.has(key) ? next.delete(key) : next.add(key);
            _state = { ..._state, states: next };
            el.classList.toggle('on', next.has(key));
            emit();
        });
    });

    // Partner dropdown
    bar.querySelector<HTMLSelectElement>('.js-partner-agency')?.addEventListener('change', e => {
        const val = (e.target as HTMLSelectElement).value;
        _state = { ..._state, partnerAgencyFilter: val ? parseInt(val) : null };
        rebuildAccomSelect();
        emit();
    });

    // Toggles
    bar.querySelector<HTMLInputElement>('.js-active-only')?.addEventListener('change', e => {
        _state = { ..._state, activeOnly: (e.target as HTMLInputElement).checked };
        emit();
    });
    bar.querySelector<HTMLInputElement>('.js-has-price')?.addEventListener('change', e => {
        _state = { ..._state, hasPriceOnly: (e.target as HTMLInputElement).checked };
        emit();
    });
    bar.querySelector<HTMLInputElement>('.js-partners-on')?.addEventListener('change', e => {
        const checked = (e.target as HTMLInputElement).checked;
        _state = { ..._state, showPartners: checked, partnerAgencyFilter: null };
        const partnerSel = bar.querySelector<HTMLSelectElement>('.js-partner-agency');
        if (partnerSel) {
            partnerSel.disabled = !checked;
            partnerSel.value = '';
        }
        rebuildAccomSelect();
        emit();
    });
}

function wireChips(bar: HTMLElement, selector: string, field: 'types' | 'services' | 'classifs'): void {
    bar.querySelectorAll<HTMLElement>(selector).forEach(chip => {
        chip.addEventListener('click', () => {
            const val = chip.textContent!.trim();
            const next = new Set(_state[field]);
            next.has(val) ? next.delete(val) : next.add(val);
            _state = { ..._state, [field]: next };
            chip.classList.toggle('on', next.has(val));
            emit();
        });
    });
}

function rebuildAccomSelect(): void {
    const sel = _container.querySelector<HTMLSelectElement>('.js-accom');
    if (!sel) return;
    let cityAccoms = _accoms.filter(a => a.city === _state.cityId);
    if (!_state.showPartners) {
        cityAccoms = cityAccoms.filter(a => !a.isPartner);
    } else if (_state.partnerAgencyFilter != null) {
        cityAccoms = cityAccoms.filter(a => !a.isPartner || a.agencyId === _state.partnerAgencyFilter);
    }
    sel.innerHTML = `<option value="">${esc(allInCityLabel(cityAccoms.length))}</option>` +
        cityAccoms.map(a => `<option value="${a.id}"${_state.offerIdFilter === a.id ? ' selected' : ''}>${esc(a.name ?? `#${a.id}`)}</option>`).join('');
}

function rebuildPartnerSelect(): void {
    const sel = _container.querySelector<HTMLSelectElement>('.js-partner-agency');
    if (!sel) return;
    const partnerAccoms = _accoms.filter(a => a.isPartner && a.city === _state.cityId);
    const partnerMap = new Map<number, string>();
    for (const a of partnerAccoms) {
        if (!partnerMap.has(a.agencyId) && a.partnerAgencyName) {
            partnerMap.set(a.agencyId, a.partnerAgencyName);
        }
    }
    const allLabel = `${_i18n['allPartners'] || 'All partners'} (${partnerMap.size})`;
    sel.innerHTML = `<option value="">${esc(allLabel)}</option>` +
        Array.from(partnerMap.entries())
            .map(([id, name]) => `<option value="${id}"${_state.partnerAgencyFilter === id ? ' selected' : ''}>${esc(name)}</option>`)
            .join('');
    sel.disabled = !_state.showPartners;
}

function allInCityLabel(count?: number): string {
    const base = _i18n['allInCity'] || 'All in city';
    return count !== undefined ? `${base} (${count})` : base;
}

// ── Date range popover ────────────────────────────────────────────────────

function togglePicker(bar: HTMLElement): void {
    const existing = bar.querySelector('.drp-popover');
    if (existing) { existing.remove(); _pickerOpen = false; return; }
    _pickerOpen = true;
    _pickerPending = { from: _state.from, to: _state.to };
    renderPicker(bar);
}

function renderPicker(bar: HTMLElement): void {
    const anchor = bar.querySelector('.js-period-trigger')?.closest('.filter-field');
    if (!anchor) return;
    const popover = document.createElement('div');
    popover.className = 'drp-popover availability-calendar';
    rebuildPickerHtml(popover);
    (anchor as HTMLElement).appendChild(popover);

    document.addEventListener('mousedown', onOutsideClick);
}

function rebuildPickerHtml(popover: HTMLElement): void {
    const today = new Date();
    const f = _pickerPending.from;
    const t = _pickerPending.to;

    const curYear = f ? new Date(f + 'T00:00:00').getFullYear() : today.getFullYear();
    const curMonth = f ? new Date(f + 'T00:00:00').getMonth() : today.getMonth();

    const months = [
        { y: curYear, m: curMonth },
        { y: curMonth === 11 ? curYear + 1 : curYear, m: (curMonth + 1) % 12 },
    ];

    const presets = [
        { label: _i18n['thisWeek'] || 'This week', offset: 6 },
        { label: _i18n['next14'] || 'Next 14 days', offset: 14 },
        { label: _i18n['next30'] || 'Next 30 days', offset: 30 },
        { label: _i18n['next60'] || 'Next 60 days', offset: 60 },
        { label: _i18n['next90'] || 'Next 90 days', offset: 90 },
    ];

    const dows = (_i18n['dows'] || 'Su,Mo,Tu,We,Th,Fr,Sa').split(',');

    popover.innerHTML = `
      <div class="drp-quick">
        ${presets.map(p => `<button type="button" class="btn btn-ghost btn-xs js-preset" data-offset="${p.offset}">${esc(p.label)}</button>`).join('')}
      </div>
      <div class="drp-months">
        ${months.map((mo, mi) => renderPickerMonth(mo.y, mo.m, mi, f, t, dows, curYear, curMonth)).join('')}
      </div>
      <div class="drp-footer">
        <div class="drp-selected">
          <span>${esc(_i18n['from'] || 'From')} </span><strong>${f || '—'}</strong>
          <span> ${esc(_i18n['to'] || 'to')} </span><strong>${t || '—'}</strong>
        </div>
        ${(f || t) ? `<button type="button" class="btn btn-ghost btn-xs js-picker-clear" style="color:var(--bs-danger)">${_i18n['clear'] || 'Clear'}</button>` : ''}
        ${(f && t) ? `<button type="button" class="btn btn-primary btn-xs js-picker-apply">${_i18n['apply'] || 'Apply'}</button>` : ''}
      </div>
      <button type="button" class="js-prev-month" style="display:none"></button>
      <button type="button" class="js-next-month" style="display:none"></button>`;

    // Wire preset buttons
    popover.querySelectorAll<HTMLButtonElement>('.js-preset').forEach(btn => {
        btn.addEventListener('click', () => {
            const offset = parseInt(btn.dataset.offset!);
            _pickerPending = { from: toYmd(today), to: toYmd(addDays(today, offset)) };
            applyPicker();
        });
    });

    // Wire month nav
    popover.querySelector<HTMLButtonElement>('.js-prev-month')?.addEventListener('click', () => {
        shiftPickerView(popover, -1, curYear, curMonth);
    });
    popover.querySelector<HTMLButtonElement>('.js-next-month')?.addEventListener('click', () => {
        shiftPickerView(popover, 1, curYear, curMonth);
    });

    // Wire day clicks
    popover.querySelectorAll<HTMLElement>('.drp-day:not(.empty)').forEach(cell => {
        cell.addEventListener('click', () => {
            const ds = cell.dataset.date!;
            if (!_pickerPending.from || (_pickerPending.from && _pickerPending.to)) {
                _pickerPending = { from: ds, to: null };
            } else {
                if (ds < _pickerPending.from!) {
                    _pickerPending = { from: ds, to: _pickerPending.from };
                } else {
                    _pickerPending = { from: _pickerPending.from, to: ds };
                }
                applyPicker();
            }
            rebuildPickerHtml(popover);
        });
    });

    // Clear / Apply
    popover.querySelector('.js-picker-clear')?.addEventListener('click', () => {
        _pickerPending = { from: null, to: null };
        rebuildPickerHtml(popover);
    });
    popover.querySelector('.js-picker-apply')?.addEventListener('click', applyPicker);

    // Month nav visible
    popover.querySelector<HTMLButtonElement>('.js-prev-month')!.style.display = 'inline-flex';
    popover.querySelector<HTMLButtonElement>('.js-next-month')!.style.display = 'inline-flex';
}

function shiftPickerView(popover: HTMLElement, dir: number, curYear: number, curMonth: number): void {
    let m = curMonth + dir;
    let y = curYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    // Simple re-render with new view month (we store it locally for this popover session)
    const months = [
        { y, m },
        { y: m === 11 ? y + 1 : y, m: (m + 1) % 12 },
    ];
    const dows = (_i18n['dows'] || 'Su,Mo,Tu,We,Th,Fr,Sa').split(',');
    const f = _pickerPending.from;
    const t = _pickerPending.to;
    const monthsEl = popover.querySelector('.drp-months')!;
    monthsEl.innerHTML = months.map((mo, mi) => renderPickerMonth(mo.y, mo.m, mi, f, t, dows, y, m)).join('');

    // Re-wire day clicks
    monthsEl.querySelectorAll<HTMLElement>('.drp-day:not(.empty)').forEach(cell => {
        cell.addEventListener('click', () => {
            const ds = cell.dataset.date!;
            if (!_pickerPending.from || (_pickerPending.from && _pickerPending.to)) {
                _pickerPending = { from: ds, to: null };
            } else {
                _pickerPending = ds < _pickerPending.from!
                    ? { from: ds, to: _pickerPending.from }
                    : { from: _pickerPending.from, to: ds };
                applyPicker();
            }
            rebuildPickerHtml(popover);
        });
    });
}

function renderPickerMonth(
    y: number, m: number, mi: number,
    from: string | null, to: string | null,
    dows: string[], curY: number, curM: number
): string {
    const firstDow = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const todayYmd = toYmd(new Date());
    const mNames = (_i18n['months'] || 'January,February,March,April,May,June,July,August,September,October,November,December').split(',');

    let cells = '';
    for (let i = 0; i < firstDow; i++) cells += `<div class="drp-day empty"></div>`;
    for (let d = 1; d <= daysInMonth; d++) {
        const ds = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const isStart = from === ds;
        const isEnd = to === ds;
        const isToday = ds === todayYmd;
        let cls = 'drp-day';
        if (isStart || isEnd) cls += ' range-start';
        else if (from && to && ds > from && ds < to) cls += ' in-range';
        if (isToday && !isStart && !isEnd) cls += ' today';
        cells += `<div class="${cls}" data-date="${ds}">${d}</div>`;
    }

    const prevBtn = mi === 0 ? `<button class="drp-nav js-prev-month" type="button">‹</button>` : `<div style="width:26px"></div>`;
    const nextBtn = mi === 1 ? `<button class="drp-nav js-next-month" type="button">›</button>` : `<div style="width:26px"></div>`;

    return `
      <div class="drp-month">
        <div class="drp-month-header">
          ${prevBtn}
          <span class="drp-month-title">${mNames[m] || ''} ${y}</span>
          ${nextBtn}
        </div>
        <div class="drp-grid">
          ${dows.map(d => `<div class="drp-dow">${d}</div>`).join('')}
          ${cells}
        </div>
      </div>`;
}

function applyPicker(): void {
    if (_pickerPending.from && _pickerPending.to) {
        _state = { ..._state, from: _pickerPending.from, to: _pickerPending.to };
        closePicker();
        emit();
    }
}

function closePicker(): void {
    _container.querySelector('.drp-popover')?.remove();
    _pickerOpen = false;
    document.removeEventListener('mousedown', onOutsideClick);
}

function onOutsideClick(e: MouseEvent): void {
    const popover = _container.querySelector('.drp-popover');
    if (popover && !popover.contains(e.target as Node)) {
        closePicker();
    }
}

export function shiftWindow(days: number, onChange: FilterChangeCallback): void {
    const f = addDays(new Date(_state.from + 'T00:00:00'), days);
    const t = addDays(new Date(_state.to + 'T00:00:00'), days);
    _state = { ..._state, from: toYmd(f), to: toYmd(t) };
    onChange(_state);
}

export function resetToToday(onChange: FilterChangeCallback): void {
    const today = new Date();
    _state = { ..._state, from: toYmd(today), to: toYmd(addDays(today, 27)) };
    onChange(_state);
}

function periodLabel(from: string, to: string): string {
    if (!from || !to) return _i18n['choosePeriod'] || 'Choose period';
    const mNames = (_i18n['months'] || '').split(',');
    return `${fmtPretty(from, mNames)} → ${fmtPretty(to, mNames)} · ${daysBetween(from, to)} ${_i18n['nights'] || 'nights'}`;
}

function emit(): void { _onChange(_state); }

function chip(label: string, on: boolean): string {
    return `<span class="chip${on ? ' on' : ''}">${esc(label)}</span>`;
}

function esc(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
